import DOMPurify, { clearWindow, type ElementHook } from 'isomorphic-dompurify'

export const SERVER_CLEAR_THRESHOLD = 50_000

export const SANITIZE_CACHE_MAX_CHARS = 5_000_000

export const SANITIZE_CACHE_MAX_ENTRIES = 10_000

export const SANITIZE_CACHE_MAX_ENTRY_CHARS = 50_000

const HTML_CONFIG = {
  ADD_ATTR: ['target'],
}

const SVG_CONFIG = {
  USE_PROFILES: { svg: true, svgFilters: true },
} as const

const SANITIZE_CONFIG = { html: HTML_CONFIG, svg: SVG_CONFIG }

type SanitizeProfile = keyof typeof SANITIZE_CONFIG

let serverSanitizeCount = 0

const sanitizeCache = new Map<string, string>()
let sanitizeCacheChars = 0

const forceRelOnTargetLinks: ElementHook = (node) => {
  if (node.nodeName !== 'A' || !node.getAttribute('target')) return
  const rel = node.getAttribute('rel')
  if (!rel) {
    node.setAttribute('rel', 'noopener')
    return
  }
  if (!/\bnoopener\b/.test(rel)) {
    node.setAttribute('rel', `${rel} noopener`)
  }
}

// Imagem de rich-text quase nunca é o LCP, então nasce `lazy` para não competir
// com o carregamento inicial (Core Web Vitals). Só a `<img>`: `loading` não é
// atributo válido em `<picture>`/`<source>` (o browser ignora). O `<image>` do
// SVG tem nodeName minúsculo, então este hook nunca toca ícone. Respeita um
// `loading` que o autor já tenha definido (ex.: `eager` proposital).
const lazyLoadImages: ElementHook = (node) => {
  if (node.nodeName !== 'IMG' || node.hasAttribute('loading')) return
  node.setAttribute('loading', 'lazy')
}

const registerHooks = () => {
  DOMPurify.addHook('afterSanitizeAttributes', forceRelOnTargetLinks)
  DOMPurify.addHook('afterSanitizeAttributes', lazyLoadImages)
}

registerHooks()

const releaseServerResources = () => {
  if (typeof window !== 'undefined') return
  serverSanitizeCount += 1
  if (serverSanitizeCount < SERVER_CLEAR_THRESHOLD) return
  serverSanitizeCount = 0
  clearWindow()
  registerHooks()
}

export const clearSanitizeCache = (): void => {
  sanitizeCache.clear()
  sanitizeCacheChars = 0
}

const cacheEntryChars = (key: string, clean: string) => key.length + clean.length

const sanitizeCacheFits = () =>
  sanitizeCacheChars <= SANITIZE_CACHE_MAX_CHARS && sanitizeCache.size <= SANITIZE_CACHE_MAX_ENTRIES

const pruneSanitizeCache = () => {
  for (const [key, clean] of sanitizeCache) {
    if (sanitizeCacheFits()) return
    sanitizeCache.delete(key)
    sanitizeCacheChars -= cacheEntryChars(key, clean)
  }
}

const rememberSanitized = (key: string, clean: string) => {
  const chars = cacheEntryChars(key, clean)
  if (chars > SANITIZE_CACHE_MAX_ENTRY_CHARS) return
  sanitizeCache.set(key, clean)
  sanitizeCacheChars += chars
  pruneSanitizeCache()
}

const sanitizeWith = (profile: SanitizeProfile, dirty: string) =>
  DOMPurify.sanitize(dirty, SANITIZE_CONFIG[profile]) as string

const sanitizeCached = (profile: SanitizeProfile, dirty: string): string => {
  if (typeof dirty !== 'string' || !dirty.includes('<')) return sanitizeWith(profile, dirty)
  const key = `${profile}:${dirty}`
  const cached = sanitizeCache.get(key)
  if (cached !== undefined) {
    sanitizeCache.delete(key)
    sanitizeCache.set(key, cached)
    return cached
  }
  const clean = sanitizeWith(profile, dirty)
  releaseServerResources()
  rememberSanitized(key, clean)
  return clean
}

export const sanitizeHtml = (dirty?: string | null): string => {
  if (!dirty) return ''
  return sanitizeCached('html', dirty)
}

export const sanitizeSvg = (dirty?: string | null): string => {
  if (!dirty) return ''
  return sanitizeCached('svg', dirty)
}

const HTML_BREAKOUT = /[<>&\u2028\u2029]/g

const JSON_ESCAPES: Record<string, string> = {
  '<': '\\u003c',
  '>': '\\u003e',
  '&': '\\u0026',
  '\u2028': '\\u2028',
  '\u2029': '\\u2029',
}

// JSON seguro para `<script>` inline: escapa o que fecharia a tag, sem
// alterar o valor depois do `JSON.parse`.
export const sanitizeJsonForHtml = (value: unknown): string =>
  JSON.stringify(value).replace(HTML_BREAKOUT, (char) => JSON_ESCAPES[char])
